import { useState, useEffect, useCallback } from "react";
import { T, F, Over, Pill, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// O. Reg. 579/05 s.17 trade record sheet: draft from deal+ledger, agent sign, BoR sign, initialled corrections.
const cad = (cents) => cents == null ? '—' : new Intl.NumberFormat('en-CA', { style:'currency', currency:'CAD' }).format((cents || 0) / 100);
const STATUS = { draft:['warn','Draft'], agent_signed:['warn','Agent signed'], bor_signed:['ok','BoR signed'] };

export default function TrustTradeRecord({ BASE, headers, p, dark, deals, canAuth }) {
  const [open, setOpen] = useState(false);
  const [dealId, setDealId] = useState('');
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [corr, setCorr] = useState({ field:'buyer_names', new_value:'', note:'' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (id) => {
    if (!id) { setData(null); return; }
    const res = await fetch(`${BASE}/api/accounting/trust/trades/${id}/record-sheet`, { headers });
    if (!res.ok) { setData(null); return; }
    setData(await res.json());
  }, [BASE, headers.Authorization]); // eslint-disable-line

  useEffect(() => { if (open && dealId) load(dealId); }, [open, dealId, load]);

  const call = async (method, path, body, ok) => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/api/accounting/trust/trades/${dealId}/record-sheet${path}`, {
        method, headers, body: body != null ? JSON.stringify(body) : undefined,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'That didn’t go through');
      if (ok) toast.success(ok);
      await load(dealId);
      return d;
    } catch (e) { toast.error(e.message); return null; }
    finally { setBusy(false); }
  };

  const input = { marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' };
  const sheet = data?.sheet;
  const effective = data?.effective || sheet;
  const [tone, label] = STATUS[sheet?.status] || ['neutral', 'None'];

  const start = () => {
    const first = (deals || [])[0]?.id || '';
    setDealId(first); setData(null); setOpen(true);
  };

  const syncForm = () => {
    if (!effective) return;
    setForm({
      nature_of_trade: effective.nature_of_trade || '',
      property_address: effective.property_address || '',
      consideration: effective.consideration_cents != null ? (effective.consideration_cents / 100).toFixed(2) : '',
      buyer_names: effective.buyer_names || '',
      seller_names: effective.seller_names || '',
      buyer_lawyer: effective.buyer_lawyer || '',
      seller_lawyer: effective.seller_lawyer || '',
      listing_registrant: effective.listing_registrant || '',
      cooperating_registrant: effective.cooperating_registrant || '',
      remuneration: effective.remuneration_cents != null ? (effective.remuneration_cents / 100).toFixed(2) : '',
      expected_completion_date: (effective.expected_completion_date || '').slice(0, 10),
      actual_completion_date: (effective.actual_completion_date || '').slice(0, 10),
    });
  };

  useEffect(() => { syncForm(); }, [data?.sheet?.id, data?.sheet?.updated_at, data?.corrections?.length]); // eslint-disable-line

  const field = (label, key, extra = {}) => (
    <div><Over color={p.ink3}>{label}</Over>
      <input value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
        style={{ ...input, ...(extra.mono ? { fontFamily:F.mono } : {}) }} type={extra.type || 'text'} disabled={!data?.can_edit || busy}/>
    </div>
  );

  return (
    <>
      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <Btn variant="ghost" dark={dark} onClick={start}>Trade record sheet</Btn>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ maxWidth:640, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)', maxHeight:'90vh', overflowY:'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>Trade record sheet</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>
              O. Reg. 579/05 s.17 — deposit and disbursement from the trust ledger. Corrections after signing are initialled, not obliterated.
            </DialogDescription>
          </DialogHeader>

          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:16 }}>
            <div><Over color={p.ink3}>Deal</Over>
              <select style={input} value={dealId} onChange={e => setDealId(e.target.value)}>
                <option value="">Pick a deal</option>
                {(deals || []).map(d => <option key={d.id} value={d.id}>{d.property_address}{d.mls_number ? ` · ${d.mls_number}` : ''}</option>)}
              </select>
            </div>

            {dealId && (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <Pill tone={tone} dark={dark}>{label}</Pill>
                <Btn variant="ghost" dark={dark} disabled={busy} onClick={() => call('POST', '', null, sheet ? 'Draft refreshed from deal + ledger' : 'Draft created')}>
                  {sheet ? 'Refresh from deal + ledger' : 'Create draft from deal + ledger'}
                </Btn>
                {sheet && (
                  <>
                    <a href={`${BASE}/api/accounting/trust/trades/${dealId}/record-sheet/export.csv`} style={{ fontSize:12, fontFamily:F.mono, color:T.brick }}
                      onClick={async (e) => {
                        e.preventDefault();
                        const res = await fetch(`${BASE}/api/accounting/trust/trades/${dealId}/record-sheet/export.csv`, { headers });
                        if (!res.ok) { toast.error('Export failed'); return; }
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url; a.download = `trs-${dealId.slice(0,8)}.csv`; a.click();
                        URL.revokeObjectURL(url);
                      }}>Export CSV</a>
                    <a href={`${BASE}/api/accounting/trust/trades/${dealId}/record-sheet/export.txt`} style={{ fontSize:12, fontFamily:F.mono, color:T.brick }}
                      onClick={async (e) => {
                        e.preventDefault();
                        const res = await fetch(`${BASE}/api/accounting/trust/trades/${dealId}/record-sheet/export.txt`, { headers });
                        if (!res.ok) { toast.error('Export failed'); return; }
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url; a.download = `trs-${dealId.slice(0,8)}.txt`; a.click();
                        URL.revokeObjectURL(url);
                      }}>Export TXT</a>
                  </>
                )}
              </div>
            )}

            {sheet && (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  {field('Nature of trade', 'nature_of_trade')}
                  {field('Property', 'property_address')}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  {field('Consideration (CAD)', 'consideration', { mono:true })}
                  {field('Remuneration (CAD)', 'remuneration', { mono:true })}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  {field('Buyer(s)', 'buyer_names')}
                  {field('Seller(s)', 'seller_names')}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  {field("Buyer's lawyer", 'buyer_lawyer')}
                  {field("Seller's lawyer", 'seller_lawyer')}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  {field('Listing registrant', 'listing_registrant')}
                  {field('Co-operating registrant', 'cooperating_registrant')}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                  {field('Expected completion', 'expected_completion_date', { type:'date' })}
                  {field('Actual completion', 'actual_completion_date', { type:'date' })}
                </div>

                <div style={{ border:`1px solid ${p.hair}`, borderRadius:2, padding:14 }}>
                  <Over color={p.ink3}>Deposit & disbursement (from trust ledger · read-only)</Over>
                  <div style={{ marginTop:8, fontSize:13 }}>Deposit held: <span style={{ fontFamily:F.mono }}>{cad(sheet.deposit_amount_cents ?? data?.ledger_summary?.deposit_amount_cents)}</span></div>
                  {(sheet.deposit_disbursement_summary || []).length === 0 ? (
                    <div style={{ fontSize:12, color:p.ink3, marginTop:6 }}>No disbursements yet.</div>
                  ) : (sheet.deposit_disbursement_summary || []).map((d, i) => (
                    <div key={i} style={{ fontSize:12, color:p.ink2, marginTop:4, fontFamily:F.mono }}>
                      {cad(d.amount_cents)} → {d.payee || '—'} · {d.purpose || d.kind || ''}
                    </div>
                  ))}
                </div>

                {data?.can_edit && (
                  <Btn variant="ghost" dark={dark} disabled={busy} onClick={() => call('PATCH', '', form, 'Draft saved')}>Save draft fields</Btn>
                )}

                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {data?.can_sign_agent && sheet.status === 'draft' && (
                    <Btn variant="primary" dark={dark} disabled={busy} onClick={() => call('POST', '/sign-agent', null, 'Agent signature recorded')}>Sign as salesperson</Btn>
                  )}
                  {data?.can_sign_bor && sheet.status === 'agent_signed' && (
                    <Btn variant="primary" dark={dark} disabled={busy} onClick={() => call('POST', '/sign-bor', null, 'Broker of record signature recorded')}>Sign as broker of record</Btn>
                  )}
                </div>

                {sheet.status !== 'draft' && (
                  <div style={{ borderTop:`1px solid ${p.hair}`, paddingTop:14 }}>
                    <Over color={p.ink3}>Correction (initialled · not obliterated)</Over>
                    <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1.4fr 1fr', gap:10, marginTop:8 }}>
                      <select style={input} value={corr.field} onChange={e => setCorr({ ...corr, field: e.target.value })}>
                        {['nature_of_trade','property_address','consideration_cents','buyer_names','seller_names','buyer_lawyer','seller_lawyer','listing_registrant','cooperating_registrant','deposit_amount_cents','remuneration_cents','expected_completion_date','actual_completion_date'].map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                      <input style={input} value={corr.new_value} onChange={e => setCorr({ ...corr, new_value: e.target.value })} placeholder="New value"/>
                      <input style={input} value={corr.note} onChange={e => setCorr({ ...corr, note: e.target.value })} placeholder="Note (optional)"/>
                    </div>
                    <div style={{ marginTop:10 }}>
                      <Btn variant="ghost" dark={dark} disabled={busy} onClick={() => call('POST', '/correct', corr, 'Correction initialled')}>Initial correction</Btn>
                    </div>
                    {(data?.corrections || []).length > 0 && (
                      <div style={{ marginTop:12 }}>
                        {(data.corrections || []).map(c => (
                          <div key={c.id} style={{ fontSize:12, color:p.ink3, marginTop:4 }}>
                            {c.field}: {c.old_value ?? '—'} → {c.new_value ?? '—'} · {c.initialled_by_name} · {c.initialled_at?.slice(0, 10)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ fontSize:12, color:p.ink3 }}>
                  Agent: {sheet.agent_signed_by_name || '—'}{sheet.agent_signed_at ? ` · ${sheet.agent_signed_at.slice(0,10)}` : ''}
                  {' · '}BoR: {sheet.bor_signed_by_name || '—'}{sheet.bor_signed_at ? ` · ${sheet.bor_signed_at.slice(0,10)}` : ''}
                </div>
              </>
            )}
          </div>

          <DialogFooter style={{ marginTop:18 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setOpen(false)}>Close</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
